import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";
import WaveSurfer from "wavesurfer.js";

interface AudioTimeDisplayProps {
  wavesurferInstance: WaveSurfer | null;
  isPlaying: boolean;
}

// 秒数を「mm:ss」形式にフォーマットする関数
const formatTime = (timeInSeconds: number): string => {
  if (isNaN(timeInSeconds)) return "00:00";

  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

// 時間表示のみを担当する独立したコンポーネント
const AudioTimeDisplay: React.FC<AudioTimeDisplayProps> = ({ wavesurferInstance, isPlaying }) => {
  // コンポーネント内で時間管理
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const timeUpdateRef = useRef<number | null>(null);

  // 再生時間の更新
  useEffect(() => {
    // 初期化
    if (wavesurferInstance) {
      // 総時間を取得（再生状態に関わらず）
      setDuration(wavesurferInstance.getDuration() || 0);

      if (isPlaying) {
        // 再生中は定期的に現在時間を更新（アニメーションフレーム使用でレンダリング最適化）
        const updateTime = () => {
          if (wavesurferInstance && isPlaying) {
            setCurrentTime(wavesurferInstance.getCurrentTime() || 0);
            timeUpdateRef.current = requestAnimationFrame(updateTime);
          }
        };

        timeUpdateRef.current = requestAnimationFrame(updateTime);
      } else if (timeUpdateRef.current) {
        // 再生停止時はアニメーションフレームをクリーンアップ
        cancelAnimationFrame(timeUpdateRef.current);
      }
    }

    // クリーンアップ関数
    return () => {
      if (timeUpdateRef.current) {
        cancelAnimationFrame(timeUpdateRef.current);
      }
    };
  }, [wavesurferInstance, isPlaying]);

  return (
    <Typography variant="caption" sx={{ whiteSpace: "nowrap", minWidth: 82 }}>
      {formatTime(currentTime)} / {formatTime(duration)}
    </Typography>
  );
};

export default AudioTimeDisplay;
